import { andFilters } from './filter'

/**
 * Dashboard + page filter compounding (#1183).
 *
 * A dashboard filter applies to every page; a page filter applies to one
 * page. They ALWAYS compound: the resolved filter is
 *
 *   (dashboard filter) AND (page filter)
 *
 * in Carbon case-algebra syntax (`&` is AND — see docs/carbon.md). Each side
 * is wrapped in parentheses BEFORE joining so an OR (`|`) inside either
 * expression cannot change precedence. An empty side is ignored — never
 * `() & (page)` — and two empty sides resolve to no filter at all.
 */
export function compoundFilters(dashboardFilter?: string, pageFilter?: string): string {
  return andFilters(dashboardFilter, pageFilter)
}

/**
 * Per-job filters (#1162). On a multi-job dashboard a filter expression is
 * Carbon syntax over ONE job's variable universe, so the dashboard- and
 * page-level filters are stored per job: `global_filters[job]` /
 * `page.filters[job]`. A single-job dashboard keeps the legacy plain strings
 * (`global_filter` / `page.filter`), which are read as the entry for the
 * PRIMARY job — so the single-job path resolves byte-for-byte as before and
 * needs no migration.
 *
 * The dashboard and page sides still ALWAYS compound as (dashboard)&(page)
 * via compoundFilters. A job with no expression on either side is
 * unfiltered — never another job's expression.
 */
type PerJobFilterConfig = {
  global_filter?: string
  global_filters?: Record<string, string>
}
type PerJobFilterPage = {
  filter?: string
  filters?: Record<string, string>
}

function filterForJob(
  legacy: string | undefined,
  map: Record<string, string> | undefined,
  job: string,
  primaryJob: string,
): string {
  // The map form wins when present; the legacy string is the primary job's
  // entry. An empty widget job (legacy widgets carry none) is treated as the
  // primary job.
  const effectiveJob = job || primaryJob
  if (map && Object.keys(map).length > 0) return map[effectiveJob] || ''
  return effectiveJob === primaryJob ? (legacy || '') : ''
}

/**
 * One job's PAGE-side filter, read from whichever home the page uses — the
 * per-job map when it exists, else the legacy string. The single-job editor
 * must read through this rather than `page.filter` directly: a dashboard
 * that was multi-job, wrote its page filter into the map, and then dropped
 * back to one job still carries the map, and the viewer resolves through
 * it — so reading the legacy string shows "None" while the reader stays
 * filtered.
 */
export function pageFilterForJob(
  page: PerJobFilterPage | undefined,
  job: string,
  primaryJob: string,
): string {
  return filterForJob(page?.filter, page?.filters, job, primaryJob)
}

export function resolveWidgetFilter(
  config: PerJobFilterConfig | undefined,
  page: PerJobFilterPage | undefined,
  widgetJob: string,
  primaryJob: string,
): string {
  const dashboard = filterForJob(config?.global_filter, config?.global_filters, widgetJob, primaryJob)
  const pageSide = filterForJob(page?.filter, page?.filters, widgetJob, primaryJob)
  return compoundFilters(dashboard, pageSide)
}

/**
 * The resolved per-job filter map the dashboard layout hands down: every
 * dashboard job → its compound (dashboard AND page) filter. Widgets pick
 * their own job's entry. Single-job dashboards yield one entry, identical to
 * today's single resolved string.
 */
export function jobFilterMap(
  config: PerJobFilterConfig | undefined,
  page: PerJobFilterPage | undefined,
  dashboardJobs: string[],
  primaryJob: string,
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const job of dashboardJobs) {
    out[job] = resolveWidgetFilter(config, page, job, primaryJob)
  }
  return out
}

/**
 * Gen AI card filters (#1810). A card runs each of its tables under that
 * table's own job's filter, and its analysis snapshots the map
 * (`config.job_filters`). These are the card's side of it.
 *
 * `cardJobFilters` is the card's canonical map — the same shape the server
 * stores: one entry per job the card's tables use (a table with no job is
 * the widget's job), trimmed, empties dropped. On a multi-job dashboard the
 * job takes its own `jobFilters` entry; on a single-job one only the
 * widget's job gets `globalFilter` and a cross-job table gets none, as a
 * chart does (Carbon zeroes every cell for a filter naming a variable the
 * job lacks).
 */
export function cardJobFilters(
  tables: { job?: string }[],
  widgetJob: string,
  jobFilters: Record<string, string> | undefined,
  globalFilter: string | undefined,
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const t of tables) {
    const job = t.job || widgetJob
    const filter = (jobFilters ? jobFilters[job] : (job === widgetJob ? globalFilter : '')) || ''
    if (filter.trim()) out[job] = filter.trim()
  }
  return out
}

/**
 * The dashboard filter a Gen AI card on a single-job dashboard may use
 * (#1810): only when the card's own job is the dashboard's job. A legacy card
 * on another job would otherwise run its tables under a filter written for a
 * different job, and Carbon answers that with every cell zeroed. On a
 * multi-job dashboard each table takes its own job's entry, so this changes
 * nothing there.
 */
export function cardGlobalFilter(
  widgetJob: string | undefined,
  dashboardJob: string | undefined,
  jobFilters: Record<string, string> | undefined,
  globalFilter: string | undefined,
): string | undefined {
  if (jobFilters) return globalFilter
  return !widgetJob || widgetJob === dashboardJob ? globalFilter : undefined
}

/**
 * The map a card POSTs as `job_filters` when it starts an analysis (#1810):
 * the page's whole map (the server keeps only the jobs the card's tables
 * use), or on a single-job dashboard the dashboard filter for the widget's
 * job. Empty values dropped.
 */
export function cardSendJobFilters(
  widgetJob: string,
  jobFilters: Record<string, string> | undefined,
  globalFilter: string | undefined,
): Record<string, string> {
  const source = jobFilters ?? { [widgetJob]: globalFilter || '' }
  const out: Record<string, string> = {}
  for (const [job, filter] of Object.entries(source)) {
    if ((filter || '').trim()) out[job] = filter.trim()
  }
  return out
}

/** Two filter maps are equal when their non-empty, trimmed entries match; undefined is empty. */
export function jobFiltersEqual(
  a: Record<string, string> | undefined,
  b: Record<string, string> | undefined,
): boolean {
  const norm = (m: Record<string, string> | undefined) =>
    Object.entries(m || {}).filter(([, v]) => (v || '').trim()).map(([k, v]) => [k, v.trim()] as const)
  const na = norm(a)
  const nb = new Map(norm(b))
  return na.length === nb.size && na.every(([k, v]) => nb.get(k) === v)
}

/**
 * Quick filters (#1229 C7, Matt 3 Sep). A viewer's quick filter is
 * viewer-local, held per job, and APPENDS to the stored (dashboard AND page)
 * filter for that job — it never replaces it: an editor named a page
 * "18–34 year olds" for a reason, and a quick filter must not take that
 * meaning away. Compounded with the same parenthesised AND as everything
 * else. Quick filters for jobs the dashboard does not have are ignored.
 */
export function applyQuickFilters(
  stored: Record<string, string>,
  quick: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const job of Object.keys(stored)) {
    out[job] = compoundFilters(stored[job], quick[job])
  }
  return out
}

/**
 * Write one job's page filter (#1162), folding the legacy single-job string
 * away first (#1240 A2). A page that was authored before the dashboard
 * became multi-job carries `page.filter`; `filterForJob` reads it as the
 * primary job's entry only while the map is empty, so if the string is left
 * in place it resurfaces the moment the last per-job filter is deleted —
 * the bar says "none", the widget still runs under it. On the first per-job
 * write the string becomes `filters[primaryJob]` (an existing map entry for
 * the primary wins) and `filter` is cleared, so there is one home.
 */
export function writeJobPageFilter<T extends PerJobFilterPage>(
  page: T,
  primaryJob: string,
  job: string,
  expr: string,
): T {
  const filters = { ...(page.filters || {}) }
  if (page.filter?.trim() && primaryJob && !filters[primaryJob]) filters[primaryJob] = page.filter.trim()
  if (expr) filters[job] = expr
  else delete filters[job]
  const next = { ...page, filters }
  delete next.filter
  return next
}

/**
 * Insights framing lines (#1240 B1, Matt 3 Sep). Rendered at the top of the
 * pane for the reader; the server builds the model's "Data sources" block
 * from the SAME fields with the same rule (goapi/pkg/prompts
 * DashboardInsightsSourceLines) — keep the two in step. A multi-job view
 * opens with the multiple-sources line; then one line per job with its
 * display name and the page/quick filters on it.
 */
export interface InsightsSourceLine {
  job: string
  display_name?: string
  page_filter?: string
  quick_filter?: string
}

/**
 * Closes the framing when any source is filtered (#1240): the pane reads
 * table data UNDER the filter now, so the reader is told the filter was
 * applied, not ignored. Mirrored verbatim by DashboardInsightsFilterAppliedLine
 * in goapi/pkg/prompts/dashboard_insights.go — the model is told the same.
 */
export const INSIGHTS_FILTER_APPLIED_LINE =
  'Figures in this pane are read with these filters applied. Gen AI card text was generated under the filters in force when it was last run; a card says when they have changed.'

export function insightsSourceLines(sources: InsightsSourceLine[]): string[] {
  if (sources.length === 0) return []
  const lines: string[] = []
  if (sources.length > 1) lines.push('This dashboard references multiple data sources.')
  let anyFilter = false
  for (const src of sources) {
    const name = (src.display_name || '').trim() || src.job
    const page = (src.page_filter || '').trim()
    const quick = (src.quick_filter || '').trim()
    if (page && quick) lines.push(`${name} — filtered by ${page} and quick filter ${quick}`)
    else if (page) lines.push(`${name} — filtered by ${page}`)
    else if (quick) lines.push(`${name} — quick filter ${quick}`)
    else lines.push(`${name} — no filter`)
    if (page || quick) anyFilter = true
  }
  if (anyFilter) lines.push(INSIGHTS_FILTER_APPLIED_LINE)
  return lines
}

/**
 * Write the single-job page filter into the home the viewer reads. A page
 * that never had a map keeps the legacy string (no migration of untouched
 * single-job dashboards); a page that carries a map — a dashboard that was
 * multi-job and collapsed to one job — is written through the map so the
 * change is the one the viewer applies.
 */
export function writePageFilter<T extends PerJobFilterPage>(
  page: T,
  primaryJob: string,
  expr: string,
): T {
  if (page.filters && Object.keys(page.filters).length > 0) {
    return writeJobPageFilter(page, primaryJob, primaryJob, expr)
  }
  return { ...page, filter: expr || undefined }
}
